import React from 'react';
import { useTranslation } from 'utils/i18n';
import {
  getBasics,
  getBookingsCancellations,
  getGettingStarted,
  getSetupPayments,
  getUsingRoomOnSite,
} from '../../constants/faqs';
import FrequentlyAskedQuestionsItems from './frequentlyAskedQuestionsItems';

interface QuestionAnswer {
  question: string;
  answer?: string;
  roomTypes?: { clinical: Array<string>; nonClinical: Array<string> };
  introText?: string;
  endText?: string;
  listItems?: Array<string>;
  formattedText?: boolean;
  hyperlinks?: Array<{ url: string; name: string }>;
  hyperlinksIntroText?: string;
}

interface ListItem {
  href?: string;
  text: string;
}

const FrequentlyAskedQuestions: React.FC = () => {
  const { t } = useTranslation('common');

  const renderBackToTop = () => {
    return (
      <a
        className="button button--right button--text button--link"
        href="#faqIntro"
      >
        {t('shared.back_to_top')}
      </a>
    );
  };

  const renderQuestionsAnswers = (item: QuestionAnswer) => {
    return (
      <FrequentlyAskedQuestionsItems
        question={item.question}
        answer={item.answer ?? ''}
        roomTypes={item.roomTypes}
        introText={item.introText}
        endText={item.endText}
        listItems={item.listItems}
        formattedText={item.formattedText}
        hyperlinks={item.hyperlinks}
        hyperlinksIntroText={item.hyperlinksIntroText ?? ''}
      />
    );
  };

  const renderGettingStartedList = () => {
    const listItems = t('faq.topics.how_to_use.linked_list', {
      returnObjects: true,
    });

    // Type guard to ensure it's an array
    if (!Array.isArray(listItems)) {
      return null;
    }

    return (
      <ul className="faq__formatted-list">
        {(listItems as ListItem[]).map((item, index) => (
          <li key={index}>
            {item.href ? (
              <a href={item.href} target="_blank" rel="noreferrer">
                {item.text}
              </a>
            ) : (
              item.text
            )}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
      <span id="howToUse">&nbsp;</span>
      <h3>{t('faq.topics.how_to_use.title')}</h3>
      <div className="faq__topics">
        <p>
          {t('faq.topics.how_to_use.intro_text_start')}
          <a
            href={t('faq.topics.how_to_use.intro_text_hyperlink.link')}
            target="_blank"
            rel="noreferrer"
          >
            {t('faq.topics.how_to_use.intro_text_hyperlink.text')}
          </a>
          {t('faq.topics.how_to_use.intro_text_end')}
        </p>
        <br />
        <p>{t('faq.topics.how_to_use.list_items_intro')}</p>
        {renderGettingStartedList()}
        {renderBackToTop()}
      </div>

      <span id="basics">&nbsp;</span>
      <h3>{t('faq.topics.basics.title')}</h3>
      <div className="faq__topics">
        {getBasics().map(item => renderQuestionsAnswers(item))}
        {renderBackToTop()}
      </div>

      <span id="gettingStarted">&nbsp;</span>
      <h3>{t('faq.topics.getting_started.title')}</h3>
      <div className="faq__topics">
        {getGettingStarted().map(item => renderQuestionsAnswers(item))}
        {renderBackToTop()}
      </div>

      <span id="setupPayments">&nbsp;</span>
      <h3>{t('faq.topics.setup_payments.title')}</h3>
      <div className="faq__topics">
        {getSetupPayments().map(item => renderQuestionsAnswers(item))}
        {renderBackToTop()}
      </div>

      <span id="bookingsCancellations">&nbsp;</span>
      <h3>{t('faq.topics.bookings_cancellations.title')}</h3>
      <div className="faq__topics">
        {getBookingsCancellations().map(item => renderQuestionsAnswers(item))}
        {renderBackToTop()}
      </div>

      <span id="usingRoomsOnSite">&nbsp;</span>
      <h3>{t('faq.topics.using_room_on_site.title')}</h3>
      <div className="faq__topics">
        {getUsingRoomOnSite().map(item => renderQuestionsAnswers(item))}
        {renderBackToTop()}
      </div>

      <h4>{t('faq.property_manager_guidance.title')}</h4>

      <p>{t('faq.property_manager_guidance.intro_text')}</p>

      <p>{t('faq.property_manager_guidance.landlord_text')}</p>

      <ul className="unstyled" style={{ marginTop: '20px' }}>
        <li>
          <b>{t('faq.chp_contact_intro')}</b>
        </li>
        <li>
          {t('faq.email')}:{' '}
          <a href={`mailto:${t('faq.chp_email')}`}>{t('faq.chp_email')}</a>
        </li>
      </ul>
      <br />
      <ul className="unstyled">
        <li>
          <b>{t('faq.nhs_contact_intro')}</b>
        </li>
        <li>
          {t('faq.email')}:{' '}
          <a href={`mailto:${t('faq.nhs_email')}`}>{t('faq.nhs_email')}</a>
        </li>
        <li>
          {t('faq.telephone')}: {t('faq.nhs_phone')}
        </li>
      </ul>
    </>
  );
};

export default FrequentlyAskedQuestions;
